import type { BillingCheckoutRequest, BillingStatus } from './analytics';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

// V173 — B2B invoicing details (company, VAT/BTW, KVK, address, billing
// email). All fields optional; the backend normalizes (VAT uppercased,
// email lowercased) and validates formats. Owner-only on GET and PUT.
export interface BillingProfile {
  workspaceId: string;
  /** 'business' | 'private' | null — V174 checkout gate. */
  customerType: string | null;
  companyName: string | null;
  vatNumber: string | null;
  registrationNumber: string | null;
  billingEmail: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  countryCode: string | null;
  updatedAt: string | null;
}

export type BillingProfileRequest = Partial<Omit<BillingProfile, 'workspaceId' | 'updatedAt'>>;

export const billingApi = {
  status: (workspaceId: string) =>
    api.get<BillingStatus>(`/api/billing/workspaces/${workspaceId}/status`).then((r) => r.data),
  checkout: (workspaceId: string, body: BillingCheckoutRequest) =>
    api
      .post<{ checkoutUrl: string }>(`/api/billing/workspaces/${workspaceId}/checkout`, body)
      .then((r) => r.data),
  cancelSubscription: (workspaceId: string) =>
    api
      .delete<BillingStatus>(`/api/billing/workspaces/${workspaceId}/subscription`)
      .then((r) => r.data),
  getProfile: (workspaceId: string) =>
    api.get<BillingProfile>(`/api/billing/workspaces/${workspaceId}/profile`).then((r) => r.data),
  saveProfile: (workspaceId: string, body: BillingProfileRequest) =>
    api
      .put<BillingProfile>(`/api/billing/workspaces/${workspaceId}/profile`, body)
      .then((r) => r.data),
  listInvoices: (workspaceId: string) =>
    api
      .get<WorkspaceInvoice[]>(`/api/billing/workspaces/${workspaceId}/invoices`)
      .then((r) => r.data),
};

// V174 — Mollie sales invoices created for settled payments.
export interface WorkspaceInvoice {
  id: string;
  kind: string; // subscription_first | subscription_recurring | topup
  description: string;
  amountEur: number;
  status: string; // created | failed
  invoiceUrl: string | null;
  createdAt: string | null;
}

// V113.5 — workspace usage summary, customer-facing.
// Replaces the AI cost view on /dashboard/usage. Single endpoint
// returns spend + plan utilization + top-up balance + 30-day activity.
export interface UsagePlanSpend {
  planKey: string;
  displayName: string;
  monthlyPriceEur: number;
  renewsAt: string | null;
  planOverride: boolean;
  onTrial: boolean;
}
export interface UsageTopupSpend {
  packsThisMonth: number;
  paidThisMonthEur: number;
}
export interface UsageResource {
  resource: string;
  used: number;
  limit: number;
  unlimited: boolean;
  nearCap: boolean;
  atCap: boolean;
}
export interface UsageTopupBalanceEntry {
  purchaseId: string;
  resource: string;
  aiModelTier: string | null;
  packDisplayName: string;
  unitsTotal: number;
  unitsRemaining: number;
  pricePaidEur: number;
  purchasedAt: string;
  expiresAt: string | null;
  depleted: boolean;
}
export interface UsageActivityDay {
  date: string;
  posts: number;
  images: number;
  reelsNoAudio: number;
  reelsWithAudio: number;
  carousels: number;
}
export interface WorkspaceUsageSummary {
  month: string;
  plan: UsagePlanSpend;
  topups: UsageTopupSpend;
  totalEurThisMonth: number;
  utilization: UsageResource[];
  topupBalance: UsageTopupBalanceEntry[];
  activityTrend: UsageActivityDay[];
}

export const usageApi = {
  getSummary: (workspaceId: string) =>
    api
      .get<WorkspaceUsageSummary>(`/api/workspaces/${workspaceId}/usage/summary`)
      .then((r) => r.data),
  getWorkspaceAi: (workspaceId: string) =>
    api.get<WorkspaceAiUsage>(`/api/workspaces/${workspaceId}/usage/ai`).then((r) => r.data),
  // V114 — getAiSettings + updateAiSettings removed with the per-workspace
  // AI model column. Use /admin/ai-settings for the global model picker.
  getAdminAiSummary: (params?: { from?: string; to?: string }) =>
    api.get<AdminAiUsageSummary>('/api/admin/usage/ai', { params }).then((r) => r.data),
  getAdminAiWorkspaces: (params?: { from?: string; to?: string }) =>
    api
      .get<AdminWorkspaceAiUsage[]>('/api/admin/usage/ai/workspaces', { params })
      .then((r) => r.data),
  getPerplexitySettings: () =>
    api
      .get<{
        probeEnabled: string;
        probeModel: string;
        probeRecency: string;
        embeddingEnabled: string;
        embeddingModel: string;
      }>('/api/admin/perplexity-settings')
      .then((r) => r.data),
  updatePerplexitySettings: (data: {
    probeEnabled?: boolean;
    probeModel?: string;
    probeRecency?: string;
    embeddingEnabled?: boolean;
    embeddingModel?: string;
  }) =>
    api
      .patch<{
        probeEnabled: string;
        probeModel: string;
        probeRecency: string;
        embeddingEnabled: string;
        embeddingModel: string;
      }>('/api/admin/perplexity-settings', data)
      .then((r) => r.data),
  getAnthropicBilling: (params?: { from?: string; to?: string }) =>
    api
      .get<{
        error?: string;
        models?: {
          model: string;
          inputTokens: number;
          outputTokens: number;
          cacheReadTokens: number;
          cacheCreationTokens: number;
          costUsd: number;
        }[];
        totalCostUsd?: number;
        totalCostEur?: number;
      }>('/api/admin/usage/anthropic-billing', { params })
      .then((r) => r.data),
  getFalBilling: (params?: { from?: string; to?: string }) =>
    api
      .get<{
        username?: string;
        currentBalance?: number;
        currency?: string;
        error?: string;
        monthlyUsage?: {
          endpointId: string;
          unit: string;
          quantity: number;
          unitPrice: number;
          cost: number;
          currency: string;
        }[];
        totalCostThisMonth?: number;
      }>('/api/admin/usage/fal-billing', { params })
      .then((r) => r.data),
  getPerplexityUsage: () =>
    api
      .get<{
        totalCostUsd: number;
        thisMonthCostUsd: number;
        thisMonthProbes: number;
        thisMonthBrands: number;
        weeklyAvgCostUsd: number;
      }>('/api/admin/usage/perplexity-billing')
      .then((r) => r.data),
  getApiLogs: (params: {
    provider?: string;
    success?: boolean;
    search?: string;
    page?: number;
    size?: number;
  }) =>
    api
      .get<{
        totalElements: number;
        page: number;
        size: number;
        content: ApiCallLogRow[];
      }>('/api/admin/api-logs', { params })
      .then((r) => r.data),
  getApiLogStats: () => api.get<ApiCallLogStats>('/api/admin/api-logs/stats').then((r) => r.data),
  getApiLogDetail: (id: string) =>
    api.get<ApiCallLogRow>(`/api/admin/api-logs/${id}`).then((r) => r.data),
  getXBilling: () =>
    api
      .get<{
        error?: string;
        projectCap?: number;
        tweetsConsumed?: number;
        usagePercent?: number;
        dailyUsage?: { date: string; consumed: number }[];
      }>('/api/admin/usage/x-billing')
      .then((r) => r.data),
};

export interface EngagementItemResponse {
  id: string;
  workspaceId: string;
  brandId: string | null;
  platform: string;
  itemType: string;
  authorName: string | null;
  authorHandle: string | null;
  authorAvatarUrl: string | null;
  content: string;
  sentiment: string | null;
  status: string;
  itemCreatedAt: string | null;
  externalPostId: string | null;
  suggestion: string | null;
  postId: string | null;
  /** Reply you wrote natively on the platform (e.g. directly in the IG app),
   *  captured by the polling job. Null if no native reply was detected. */
  nativeReplyText: string | null;
  nativeReplyAt: string | null;
  nativeReplyAuthorHandle: string | null;
}

// ── Phase 12 — thread-grouped inbox (V76 + V77, feature-flagged) ───────────

export interface ThreadBrandRef {
  id: string;
  name: string;
  avatarColor: string | null;
}

export interface ThreadConnectionRef {
  id: string;
  displayName: string;
  accountType: string | null;
}

export interface ThreadPostContext {
  externalPostId: string | null;
  caption: string | null;
  thumbnailUrl: string | null;
  permalink: string | null;
  publishedAt: string | null;
  mediaType: string | null;
  hydrationStatus: string | null;
  authorAvatarUrl: string | null;
  authorHandle: string | null;
  authorName: string | null;
}

export interface ThreadLatestItem {
  id: string;
  itemType: string;
  authorUsername: string | null;
  authorName: string | null;
  preview: string | null;
  externalCreatedAt: string | null;
}

export interface ThreadSummary {
  threadKey: string;
  platform: string;
  brand: ThreadBrandRef | null;
  connection: ThreadConnectionRef | null;
  post: ThreadPostContext;
  itemCount: number;
  unreadCount: number;
  lastActivityAt: string | null;
  latestItem: ThreadLatestItem | null;
}

export interface ThreadListResponseDto {
  threads: ThreadSummary[];
  page: number;
  totalThreads: number;
}

export interface ThreadItemDto {
  id: string;
  itemType: string;
  deliverySource: string | null;
  externalItemId: string;
  parentExternalId: string | null;
  authorUsername: string | null;
  authorName: string | null;
  authorAvatarUrl: string | null;
  nativeReplyText: string | null;
  nativeReplyAt: string | null;
  nativeReplyAuthorHandle: string | null;
  content: string | null;
  hydrationStatus: string | null;
  sentiment: string | null;
  aiSuggestedReply: string | null;
  status: string;
  externalCreatedAt: string | null;
  createdAt: string | null;
}

export interface ThreadDetailResponseDto {
  threadKey: string;
  platform: string;
  brand: ThreadBrandRef | null;
  connection: ThreadConnectionRef | null;
  post: ThreadPostContext;
  items: ThreadItemDto[];
  postContextHydrated?: boolean;
}

export interface ThreadMarkReadResponseDto {
  threadKey: string;
  unreadCount: number;
  updatedCount: number;
}

/**
 * V154 — bell dropdown notifications. First type is `cadence_failed`
 * with `actionType='retry_cadence'`; the frontend wires that to a
 * POST against `/cadences/run-now` on the workspace.
 */
export interface NotificationResponse {
  id: string;
  type: string;
  title: string;
  body: string | null;
  actionType: string | null;
  actionData: Record<string, unknown> | null;
  read: boolean;
  createdAt: string;
}

export interface NotificationListResponse {
  items: NotificationResponse[];
  unread: number;
}

export const notificationsApi = {
  list: (workspaceId: string, limit = 50) =>
    api
      .get<NotificationListResponse>(`/api/workspaces/${workspaceId}/notifications`, {
        params: { limit },
      })
      .then((r) => r.data),
  markRead: (workspaceId: string, notificationId: string) =>
    api
      .post(`/api/workspaces/${workspaceId}/notifications/${notificationId}/read`)
      .then((r) => r.data),
  dismiss: (workspaceId: string, notificationId: string) =>
    api
      .post(`/api/workspaces/${workspaceId}/notifications/${notificationId}/dismiss`)
      .then((r) => r.data),
};

/**
 * V154 — fire the workspace's cadence "run now" async. Backend returns
 * 202-ish immediately; posts land in PENDING_REVIEW as they complete.
 * Wired into the bell-notification "Retry" button and the existing
 * Cadence page "Generate now" button.
 */
export const cadenceRunNowApi = {
  trigger: (workspaceId: string) =>
    api
      .post<{
        status: string;
        workspaceId: string;
      }>(`/api/workspaces/${workspaceId}/cadences/run-now`)
      .then((r) => r.data),
  /**
   * V154 — poll so the frontend can disable "Run now" while a
   * generation is in-flight. Returns `{running: boolean}`. Backend
   * keeps a pod-local set of workspace ids that currently have a
   * cadence dispatch running.
   */
  status: (workspaceId: string) =>
    api
      .get<{ running: boolean }>(`/api/workspaces/${workspaceId}/cadences/run-now/status`)
      .then((r) => r.data),
};
